import AddressAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { IFilterMaterial } from "./interfaces/filter_material.interface";
import { Observable } from "rxjs";
import { IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { OrderAttributes } from "@albatrosdeveloper/ave-models-package";
import { environment } from "environments/environment.prod";
import { IOrderMaterial } from "./interfaces/order_material.interface";
import { IOrderDetails } from "./interfaces/order_details.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { andWhere, buildQuery, CombinedFilter, FilterFn, noop, Ops, orderBy, OrderVariant, orWhere, page, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { IOptionFilterDistribution } from "./interfaces/option_filter.interface";
import { IumActive } from "./interfaces/umActive.interface";
import { DistributionGroupedByTrip } from "../../monitor/monitor-routes/interfaces/distribution.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = environment.API_AVE_ORDER_URL;
const moduleName = "distribution_stock/";

//api/order/distribution_stock
@Injectable({
  providedIn: "root",
})
export class SalesDistributionService extends AbstractService<
  AddressAttributes,
  IFilterMaterial,
  IFilterMaterial
> {
  currentFilter: CombinedFilter<OrderAttributes>;
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  postAllOrderDistribution(
    filter: IFilterMaterial
  ): Observable<IRequestResult<OrderAttributes>> {
    const API_URL: string = `${apiUrl}api/${moduleName}distribution_stock`; // Ruta correcta del API
    return this.http.post<IRequestResult<OrderAttributes>>(API_URL, filter);
  }

  postAllOrderDistributionAviable(
    file: File,
    data: IFilterMaterial
  ): Observable<IRequestResult<OrderAttributes>> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filter", JSON.stringify(data));
    const API_URL: string = `${apiUrl}api/${moduleName}distribution_stock_import_aviable`; // Ruta correcta del API

    return this.http.post<IRequestResult<OrderAttributes>>(API_URL, formData);
  }

  postDistributionCharge(filter: IFilterMaterial, data: IOrderMaterial) {
    const formData = {
      filter,
      distribution: data,
    };

    const API_URL: string = `${apiUrl}api/${moduleName}distribution_stock_charge`; // Ruta correcta del API
    return this.http.post<IRequestResult<OrderAttributes>>(API_URL, formData);
  }

  pacthOrderDetails(data: IOrderDetails[]) {
    const API_URL: string = `${apiUrl}api/${moduleName}update-details`; // Ruta correcta del API
    return this.http.post<IRequestResult<OrderAttributes>>(API_URL, data);
  }

  postAllOrderDistributionReport(
    filter: IFilterMaterial,
    options?: RequestOptions
  ): Observable<IRequestResult<any>> {
    const API_URL: string = `${apiUrl}api/${moduleName}distribution_stock_charge_report`; // Ruta correcta del API
    const headers: Record<string, any> = {};
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.post<IRequestResult<any>>(API_URL, filter);
  }

  getFileTemplateExcel(): string {
    return environment.SALE_DISTRIBUTION_LOAD_TEMPLATE || null;
  }

  findPaginationDistribution(
    option: IOptionFilterDistribution,
    options?: RequestOptions
  ): Observable<IPaginationResult<OrderAttributes>> {
    const params = new HttpParams()
      .set("id", option.id)
      .set("page", option.indexPage.toString())
      .set("limit", option.limit.toString())
      .set("sort", option?.sort ?? "ASC")
      .set("search", option?.search ?? "")
      .set("state", option?.state?.toString() ?? "0");

    const headers: Record<string, any> = {};
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    const API_URL: string = apiUrl + `api/${moduleName}get_distribution_stock`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL, {
      params,
    });
  }

  findOrderNotLoad(
    options: IPagination<OrderAttributes>,
    dataToFilter?: IFilters
  ): Observable<IPaginationResult<OrderAttributes>> {
    const hasSearch = !!options.search;
    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<OrderAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.not(true)),
        andWhere(
          "company.id" as keyof OrderAttributes,
          Ops.in(...[environment.companyId])
        ),

        // Filtro especial: ordersToFilter (solo cuando NO hay búsqueda)
        dataToFilter.ordersToFilter &&
        dataToFilter.ordersToFilter.length > 0 &&
        !hasSearch
          ? andWhere("serial", Ops.in(...dataToFilter.ordersToFilter))
          : noop(),

        // Filtros condicionales únicos
        dataToFilter.type
          ? andWhere(<any>"type.code", Ops.eq(dataToFilter.type))
          : noop(),

        dataToFilter.withTravel == "1"
          ? andWhere("trip", Ops.not(null))
          : dataToFilter.withTravel == "2"
            ? andWhere("trip", null)
            : noop(),

        // 11 filtros de arrays
        dataToFilter.documentClass && dataToFilter.documentClass.length > 0
          ? andWhere(<any>"class.code", Ops.in(...dataToFilter.documentClass))
          : noop(),

        dataToFilter.seller && dataToFilter.seller.length > 0
          ? andWhere(
              <any>"user.user_login.username",
              Ops.in(...dataToFilter.seller)
            )
          : noop(),

        dataToFilter.statusERP &&
        dataToFilter.statusERP.length > 0 &&
        !dataToFilter.statusERP.includes(null)
          ? andWhere("statusERP", Ops.in(...dataToFilter.statusERP))
          : noop(),

        dataToFilter.status &&
        dataToFilter.status.length > 0 &&
        !dataToFilter.status.includes(null)
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop(),

        dataToFilter.typeCustomer &&
        dataToFilter.typeCustomer.length > 0 &&
        !dataToFilter.typeCustomer.includes(null)
          ? andWhere(
              "shipTo.types.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.typeCustomer)
            )
          : noop(),

        dataToFilter.materialGroupId &&
        dataToFilter.materialGroupId.length > 0 &&
        !dataToFilter.materialGroupId.includes(null)
          ? andWhere(
              "detail.material.materialGroups.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialGroupId)
            )
          : noop(),

        dataToFilter.materialId &&
        dataToFilter.materialId.length > 0 &&
        !dataToFilter.materialId.includes(null)
          ? andWhere(
              "detail.material.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialId)
            )
          : noop(),

        dataToFilter.soldTo && dataToFilter.soldTo.length > 0
          ? andWhere(<any>"soldTo.id", Ops.in(...dataToFilter.soldTo))
          : noop(),

        dataToFilter.shipTo && dataToFilter.shipTo.length > 0
          ? andWhere(<any>"shipTo.id", Ops.in(...dataToFilter.shipTo))
          : noop(),

        dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
          ? andWhere(
              "salesOffice.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.salesOfficeId)
            )
          : noop(),

        dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0
          ? andWhere(
              "warehouses.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.warehouseId)
            )
          : noop(),

        dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere(
              "subRoute.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.subRouteId)
            )
          : noop(),
      ];
    };

    // Objeto 1: Búsqueda por serial (número de pedido)
    const searchBySerialQueryObject: FilterFn<OrderAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch ? andWhere("serial", Ops.like(options.search!, "i")) : noop(),
    ];

    // Objeto 2: Búsqueda por nombre del cliente (soldTo)
    const searchBySoldToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "soldTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToCodeQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.externalKeys.code" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchBySoldToCodeQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "soldTo.externalKeys.code" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 4: Búsqueda por username del vendedor
    const searchByUsernameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "user.user_login.username" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 5: Búsqueda por nombre de ruta
    const searchBySubRouteNameQueryObject: FilterFn<OrderAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "subRoute.name" as keyof OrderAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    const query = buildQuery<OrderAttributes>(
      ...searchBySerialQueryObject,
      ...searchBySoldToNameQueryObject,
      ...searchByShipToNameQueryObject,
      ...searchByShipToCodeQueryObject,
      ...searchBySoldToCodeQueryObject,
      ...searchByUsernameQueryObject,
      ...searchBySubRouteNameQueryObject,
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}get_distribution_stock/non_orders_selected_paginated?filter`;
    return this.http.post<IPaginationResult<OrderAttributes>>(
      API_URL,
      {
        filter: query,
      }
    );
  }

  findDistributionClient(
    options: IPagination<OrderAttributes>,
    dataToFilter?: IFilters
  ): Observable<IPaginationResult<OrderAttributes>> {
    const hasSearch = !!options.search;
    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<OrderAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.not(true)),
        andWhere(
          "company.id" as keyof OrderAttributes,
          Ops.in(...[environment.companyId])
        ),
        dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end
          ? andWhere(
              "loadDateTime",
              Ops.btwnDates(
                dataToFilter.pickerUpload_start,
                dataToFilter.pickerUpload_end
              )
            )
          : noop(),

        // Filtro especial: ordersToFilter (solo cuando NO hay búsqueda)
        dataToFilter.ordersToFilter &&
        dataToFilter.ordersToFilter.length > 0 &&
        !hasSearch
          ? andWhere("serial", Ops.in(...dataToFilter.ordersToFilter))
          : noop(),

        // Filtros condicionales únicos
        dataToFilter.type
          ? andWhere(<any>"type.code", Ops.eq(dataToFilter.type))
          : noop(),

        dataToFilter.withTravel == "1"
          ? andWhere("trip", Ops.not(null))
          : dataToFilter.withTravel == "2"
            ? andWhere("trip", null)
            : noop(),

        // 11 filtros de arrays
        dataToFilter.documentClass && dataToFilter.documentClass.length > 0
          ? andWhere(<any>"class.code", Ops.in(...dataToFilter.documentClass))
          : noop(),

        dataToFilter.seller && dataToFilter.seller.length > 0
          ? andWhere(
              <any>"user.user_login.username",
              Ops.in(...dataToFilter.seller)
            )
          : noop(),

        dataToFilter.statusERP &&
        dataToFilter.statusERP.length > 0 &&
        !dataToFilter.statusERP.includes(null)
          ? andWhere("statusERP", Ops.in(...dataToFilter.statusERP))
          : noop(),

        dataToFilter.status &&
        dataToFilter.status.length > 0 &&
        !dataToFilter.status.includes(null)
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop(),

        dataToFilter.typeCustomer &&
        dataToFilter.typeCustomer.length > 0 &&
        !dataToFilter.typeCustomer.includes(null)
          ? andWhere(
              "shipTo.types.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.typeCustomer)
            )
          : noop(),

        dataToFilter.materialGroupId &&
        dataToFilter.materialGroupId.length > 0 &&
        !dataToFilter.materialGroupId.includes(null)
          ? andWhere(
              "detail.material.materialGroups.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialGroupId)
            )
          : noop(),

        dataToFilter.materialId &&
        dataToFilter.materialId.length > 0 &&
        !dataToFilter.materialId.includes(null)
          ? andWhere(
              "detail.material.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialId)
            )
          : noop(),

        dataToFilter.soldTo && dataToFilter.soldTo.length > 0
          ? andWhere(<any>"soldTo.id", Ops.in(...dataToFilter.soldTo))
          : noop(),

        dataToFilter.shipTo && dataToFilter.shipTo.length > 0
          ? andWhere(<any>"shipTo.id", Ops.in(...dataToFilter.shipTo))
          : noop(),

        dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
          ? andWhere(
              "salesOffice.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.salesOfficeId)
            )
          : noop(),

        dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0
          ? andWhere(
              "warehouses.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.warehouseId)
            )
          : noop(),

        dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere(
              "subRoute.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.subRouteId)
            )
          : noop(),
      ];
    };

    // Objeto 1: Búsqueda por serial (número de pedido)
    const searchBySerialQueryObject: FilterFn<OrderAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch ? andWhere("serial", Ops.like(options.search!, "i")) : noop(),
    ];

    // Objeto 2: Búsqueda por nombre del cliente (soldTo)
    const searchBySoldToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "soldTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchBySoldToCodeQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "soldTo.externalKeys.code" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 4: Búsqueda por username del vendedor
    const searchByUsernameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "user.user_login.username" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 5: Búsqueda por nombre de ruta
    const searchBySubRouteNameQueryObject: FilterFn<OrderAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "subRoute.name" as keyof OrderAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    const query = buildQuery<OrderAttributes>(
      ...searchBySerialQueryObject,
      ...searchBySoldToNameQueryObject,
      ...searchByShipToNameQueryObject,
      ...searchBySoldToCodeQueryObject,
      ...searchByUsernameQueryObject,
      ...searchBySubRouteNameQueryObject,
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      apiUrl + `api/${moduleName}get_distribution_stock/soldto_paginated`;
    return this.http.post<IPaginationResult<OrderAttributes>>(
      API_URL,
      {
        filter: query,
      }
    );
  }

  findOrderLoad(
    options: IPagination<OrderAttributes>,
    dataToFilter?: IFilters
  ): Observable<IPaginationResult<OrderAttributes>> {
    const hasSearch = !!options.search;
    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<OrderAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.not(true)),
        andWhere(
          "company.id" as keyof OrderAttributes,
          Ops.in(...[environment.companyId])
        ),
        dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end
          ? andWhere(
              "loadDateTime",
              Ops.btwnDates(
                dataToFilter.pickerUpload_start,
                dataToFilter.pickerUpload_end
              )
            )
          : noop(),

        // Filtro especial: ordersToFilter (solo cuando NO hay búsqueda)
        dataToFilter.ordersToFilter &&
        dataToFilter.ordersToFilter.length > 0 &&
        !hasSearch
          ? andWhere("serial", Ops.in(...dataToFilter.ordersToFilter))
          : noop(),

        // Filtros condicionales únicos
        dataToFilter.type
          ? andWhere(<any>"type.code", Ops.eq(dataToFilter.type))
          : noop(),

        dataToFilter.withTravel == "1"
          ? andWhere("trip", Ops.not(null))
          : dataToFilter.withTravel == "2"
            ? andWhere("trip", null)
            : noop(),

        // 11 filtros de arrays
        dataToFilter.documentClass && dataToFilter.documentClass.length > 0
          ? andWhere(<any>"class.code", Ops.in(...dataToFilter.documentClass))
          : noop(),

        dataToFilter.seller && dataToFilter.seller.length > 0
          ? andWhere(
              <any>"user.user_login.username",
              Ops.in(...dataToFilter.seller)
            )
          : noop(),

        dataToFilter.statusERP &&
        dataToFilter.statusERP.length > 0 &&
        !dataToFilter.statusERP.includes(null)
          ? andWhere("statusERP", Ops.in(...dataToFilter.statusERP))
          : noop(),

        dataToFilter.status &&
        dataToFilter.status.length > 0 &&
        !dataToFilter.status.includes(null)
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop(),

        dataToFilter.typeCustomer &&
        dataToFilter.typeCustomer.length > 0 &&
        !dataToFilter.typeCustomer.includes(null)
          ? andWhere(
              "shipTo.types.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.typeCustomer)
            )
          : noop(),

        dataToFilter.materialGroupId &&
        dataToFilter.materialGroupId.length > 0 &&
        !dataToFilter.materialGroupId.includes(null)
          ? andWhere(
              "detail.material.materialGroups.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialGroupId)
            )
          : noop(),

        dataToFilter.materialId &&
        dataToFilter.materialId.length > 0 &&
        !dataToFilter.materialId.includes(null)
          ? andWhere(
              "detail.material.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialId)
            )
          : noop(),

        dataToFilter.soldTo && dataToFilter.soldTo.length > 0
          ? andWhere(<any>"soldTo.id", Ops.in(...dataToFilter.soldTo))
          : noop(),

        dataToFilter.shipTo && dataToFilter.shipTo.length > 0
          ? andWhere(<any>"shipTo.id", Ops.in(...dataToFilter.shipTo))
          : noop(),

        dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
          ? andWhere(
              "salesOffice.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.salesOfficeId)
            )
          : noop(),

        dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0
          ? andWhere(
              "warehouses.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.warehouseId)
            )
          : noop(),

        dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere(
              "subRoute.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.subRouteId)
            )
          : noop(),
      ];
    };

    // Objeto 1: Búsqueda por serial (número de pedido)
    const searchBySerialQueryObject: FilterFn<OrderAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch ? andWhere("serial", Ops.like(options.search!, "i")) : noop(),
    ];

    // Objeto 2: Búsqueda por nombre del cliente (soldTo)
    const searchBySoldToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "soldTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToCodeQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.externalKeys.code" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 4: Búsqueda por username del vendedor
    const searchByUsernameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "user.user_login.username" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 5: Búsqueda por nombre de ruta
    const searchBySubRouteNameQueryObject: FilterFn<OrderAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "subRoute.name" as keyof OrderAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    const query = buildQuery<OrderAttributes>(
      ...searchBySerialQueryObject,
      ...searchBySoldToNameQueryObject,
      ...searchByShipToNameQueryObject,
      ...searchByShipToCodeQueryObject,
      ...searchByUsernameQueryObject,
      ...searchBySubRouteNameQueryObject,
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );
    const API_URL: string =
      apiUrl +
      `api/${moduleName}get_distribution_stock/orders_selected_paginated`;
    return this.http.post<IPaginationResult<OrderAttributes>>(
      API_URL,
      {
        filter: query,
      }
    );
  }

  postUMTemporal(data: IumActive) {
    const API_URL: string = `${apiUrl}api/${moduleName}update_um_temporal`; // Ruta correcta del API
    return this.http.post<IRequestResult<OrderAttributes>>(API_URL, data);
  }

  getOrdersGruopByMaterialPagination(
    id: string,
    option: IOptionFilterDistribution
  ) {
    const params = new HttpParams()
      .set("page", option.indexPage.toString())
      .set("limit", option.limit.toString())
      .set("sortBy", option?.sortBy ?? "name")
      .set("sortOrder", option?.sort ?? "ASC");
    const API_URL: string =
      apiUrl +
      `api/${moduleName}get_distribution_stock/orders_grouped_by_material_paginated`;
    return this.http.post<IPaginationResult<OrderAttributes>>(
      API_URL,
      { tripId: id },
      {
        params,
      }
    );
  }

  getOrdersGruopByMaterial(id: string) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}get_distribution_stock/orders_grouped_by_material`;
    return this.http.post<DistributionGroupedByTrip[]>(API_URL, {
      tripId: id,
    });
  }

  getOrderBySerial(serial: string) {
    const API_URL: string = apiUrl + `api/${moduleName}/bySerial/${serial}`;
    return this.http.get<IRequestResult<OrderAttributes>>(API_URL);
  }
}
